/**
 * Why this tab was signed out — carried across the redirect to /login so the person is told, instead
 * of landing on an unexplained login screen.
 *
 * Only ever set by THIS tab, from a signal the server sent to the signed-in session itself (the
 * ACCOUNT_DEACTIVATED refusal of a request bearing the user's own token, or the `session:revoked`
 * event on their own socket). Held in sessionStorage — per tab, gone when the tab closes — and read
 * once. A stranger typing an email on the login page never sees it, so it discloses nothing.
 */

const KEY = 'jurivoxa.signOutNotice';

export type SignOutNotice = 'account_deactivated';

export const SIGN_OUT_NOTICE_TEXT: Record<SignOutNotice, string> = {
  account_deactivated:
    'Your account has been deactivated, so you have been signed out. Contact your chambers administrator to reactivate it.',
};

export interface NoticeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function tabStorage(): NoticeStorage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function rememberSignOutNotice(notice: SignOutNotice, storage: NoticeStorage | null = tabStorage()): void {
  storage?.setItem(KEY, notice);
}

/**
 * The notice text for this tab, WITHOUT removing it. Safe to call during render: React StrictMode
 * runs state initialisers twice in development, and a read that also removed the value made the
 * second run come back empty — so the notice never showed (found in the real browser, UAT round 2).
 * Remove it with clearSignOutNotice() after it has been displayed.
 */
export function peekSignOutNotice(storage: NoticeStorage | null = tabStorage()): string | null {
  const value = storage?.getItem(KEY) ?? null;
  if (!value) return null;
  return SIGN_OUT_NOTICE_TEXT[value as SignOutNotice] ?? null;
}

/** Forget this tab's notice once it has been shown, so a later visit to /login does not repeat it. */
export function clearSignOutNotice(storage: NoticeStorage | null = tabStorage()): void {
  storage?.removeItem(KEY);
}

/** Sign this tab out and explain why on the login page. */
export function signOutWithNotice(
  notice: SignOutNotice,
  clearSession: () => void,
  storage: NoticeStorage | null = tabStorage(),
): void {
  rememberSignOutNotice(notice, storage);
  clearSession();
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.assign('/login');
  }
}
